import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilterCardsComponent, FilterCard, FilterItem } from '../filter-cards/filter-cards.component';
import { TranslocoModule } from '@jsverse/transloco';
import { FilterStateService } from '../../services/filter-state.service';
import { LanguageRoutingService } from '../../services/language-routing.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-filter-sidebar',
  standalone: true,
  imports: [CommonModule, FilterCardsComponent, TranslocoModule],
  templateUrl: './filter-sidebar.component.html',
  styleUrls: ['./filter-sidebar.component.scss'],
})
export class FilterSidebarComponent implements OnInit, OnDestroy {
  @Output() closeMobile = new EventEmitter<void>();
  @Output() clearAll = new EventEmitter<void>();
  private destroy$ = new Subject<void>();

  salaryPeriods: any[] = [];
  salaryFilter: { minSalary: number; maxSalary: number; periodKey: string } = { minSalary: 0, maxSalary: 0, periodKey: 'month' };
  loading = true;

  filterCards: FilterCard[] = [
    {
      icon: 'fa-building',
      title: 'filters.companies',
      type: 'companies',
      items: [], // Will be populated with mock/API data
      footerText: 'filters.viewAllCompanies',
      footerLink: '', // Will be set dynamically with language
      maxItems: 5,
    },
    {
      icon: 'fa-search',
      title: 'filters.workAreas',
      type: 'items',
      items: [], // Will be populated from API
      expanded: false,
    },
    {
      icon: 'fa-briefcase',
      title: 'filters.professions',
      type: 'items',
      items: [], // Will be populated from API
      footerText: 'filters.more',
    },
    {
      icon: 'fa-graduation-cap',
      title: 'filters.requiredEducation',
      type: 'items',
      items: [], // Will be populated from API
      footerText: 'filters.more',
    },
    {
      icon: 'fa-euro-sign',
      title: 'filters.salary',
      type: 'salary',
      items: [],
    },
    {
      icon: 'fa-briefcase',
      title: 'filters.employmentType',
      type: 'items',
      items: [], // Will be populated from API
      footerText: 'filters.more',
    },
  ];

  constructor(
    private readonly filterStateService: FilterStateService,
    private readonly languageRoutingService: LanguageRoutingService
  ) {}

  ngOnInit(): void {
    // Subscribe to filter data updates
    this.filterStateService.filtersData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.updateFilterCards(data);
        // Set loading to false once we have data with items
        if (data.categories.length > 0 || data.positions.length > 0) {
          this.loading = false;
        }
      });

    // Keep the salary card in sync with the applied salary filter (URL restore,
    // clear-all, external changes).
    this.filterStateService.salaryFilter$
      .pipe(takeUntil(this.destroy$))
      .subscribe(salaryFilter => {
        this.salaryFilter = salaryFilter;
      });

    // Subscribe to selected filters to update UI state
    this.filterStateService.selectedCategoryIDs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedIDs => {
        this.updateSelectedState('filters.workAreas', selectedIDs);
      });

    this.filterStateService.selectedPositionIDs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedIDs => {
        this.updateSelectedState('filters.professions', selectedIDs);
      });

    this.filterStateService.selectedEducationLevelIDs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedIDs => {
        this.updateSelectedState('filters.requiredEducation', selectedIDs);
      });

    this.filterStateService.selectedEmploymentTypeIDs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedIDs => {
        this.updateSelectedState('filters.employmentType', selectedIDs);
      });

    this.filterStateService.selectedCompanyIDs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedIDs => {
        this.updateSelectedState('filters.companies', selectedIDs);
      });

    // Set the companies footer link with current language
    this.updateCompaniesFooterLink();
  }

  private updateCompaniesFooterLink(): void {
    const companiesCard = this.filterCards.find(card => card.title === 'filters.companies');
    if (companiesCard) {
      companiesCard.footerLink = this.languageRoutingService.getLocalizedRoute('/companies');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateFilterCards(data: { categories: any[]; positions: any[]; educationLevels: any[]; employmentTypes: any[]; salaryPeriods: any[]; companies: any[] }): void {
    // Store salary periods for passing to filter-cards component
    this.salaryPeriods = data.salaryPeriods || [];


    // Update categories
    const workAreasCard = this.filterCards.find(
      (card) => card.title === 'filters.workAreas'
    );
    if (workAreasCard) {
      const selectedIDs = this.filterStateService.getCurrentFilterState().selectedCategoryIDs;
      workAreasCard.items = data.categories.map((cat) => ({
        title: `jobCategories.${cat.categoryKey}`,
        count: cat.count,
        id: cat.id,
        selected: selectedIDs.includes(cat.id),
      }));
    }

    // Update positions
    const professionsCard = this.filterCards.find(
      (card) => card.title === 'filters.professions'
    );
    if (professionsCard) {
      const selectedIDs = this.filterStateService.getCurrentFilterState().selectedPositionIDs;
      professionsCard.items = data.positions.map((pos) => ({
        title: `jobPositions.${pos.positionKey}`,
        count: pos.count,
        id: pos.id,
        selected: selectedIDs.includes(pos.id),
      }));
    }

    // Update education levels
    const educationCard = this.filterCards.find(
      (card) => card.title === 'filters.requiredEducation'
    );
    if (educationCard) {
      const selectedIDs = this.filterStateService.getCurrentFilterState().selectedEducationLevelIDs;
      educationCard.items = data.educationLevels.map((edu) => ({
        title: `jobEducationLevels.${edu.educationKey}`,
        count: edu.count,
        id: edu.id,
        selected: selectedIDs.includes(edu.id),
      }));
    }

    // Update employment types
    const employmentCard = this.filterCards.find(
      (card) => card.title === 'filters.employmentType'
    );
    if (employmentCard) {
      const selectedIDs = this.filterStateService.getCurrentFilterState().selectedEmploymentTypeIDs;
      employmentCard.items = data.employmentTypes.map((emp) => ({
        title: `jobEmploymentTypes.${emp.employmentTypeKey}`,
        count: emp.count,
        id: emp.id,
        selected: selectedIDs.includes(emp.id),
      }));
    }

    // Update companies
    const companiesCard = this.filterCards.find(
      (card) => card.title === 'filters.companies'
    );
    if (companiesCard && data.companies) {
      const selectedIDs = this.filterStateService.getCurrentFilterState().selectedCompanyIDs;
      companiesCard.items = data.companies.map((company) => ({
        title: company.name,
        count: company.jobCount,
        id: company.id,
        selected: selectedIDs.includes(company.id),
      }));
    }
  }

  private updateSelectedState(cardTitle: string, selectedIDs: number[]): void {
    const card = this.filterCards.find(c => c.title === cardTitle);
    if (card) {
      card.items.forEach(item => {
        if (item.id !== undefined) {
          item.selected = selectedIDs.includes(item.id);
        }
      });
    }
  }

  onFilterSelected(event: { cardTitle: string; item: FilterItem }): void {
    if (!event.item.id) return;

    // Handle work areas (category) filter
    if (event.cardTitle === 'filters.workAreas') {
      this.filterStateService.toggleCategory(event.item.id);
    }

    // Handle professions (position) filter
    if (event.cardTitle === 'filters.professions') {
      this.filterStateService.togglePosition(event.item.id);
    }

    // Handle education level filter
    if (event.cardTitle === 'filters.requiredEducation') {
      this.filterStateService.toggleEducationLevel(event.item.id);
    }

    // Handle employment type filter
    if (event.cardTitle === 'filters.employmentType') {
      this.filterStateService.toggleEmploymentType(event.item.id);
    }

    // Handle company filter
    if (event.cardTitle === 'filters.companies') {
      this.filterStateService.toggleCompany(event.item.id);
    }
  }

  onSalaryFilterChanged(event: { min: number; max: number; periodKey: string }): void {
    this.filterStateService.setSalaryFilter(event.min, event.max, event.periodKey);
  }

  clearAllFilters(): void {
    this.clearAll.emit();
  }

  closeMobileFilters(): void {
    this.closeMobile.emit();
  }
}
